import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { WbSunny, NightlightRound } from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { useThemeContext } from '../context/ThemeContext';

const fadeOutRotate = keyframes`
  0% {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
  100% {
    opacity: 0;
    transform: rotate(360deg) scale(1);
  }
`;

const fadeInRotate = keyframes`
  0% {
    opacity: 0;
    transform: rotate(-360deg) scale(1);
  }
  100% {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
`;

const getAnimatedIconBoxSx = (isAnimating: boolean, isFading: boolean) => ({
  position: 'absolute' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: isFading ? 1 : 0,
  animation: isAnimating ? (
    isFading
      ? `${fadeOutRotate} 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards`
      : `${fadeInRotate} 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards`
  ) : 'none',
});

type ThemeToggleProps = {
  iconColor?: string;
};

const ThemeToggle: React.FC<ThemeToggleProps> = ({ iconColor = 'white' }) => {
  const { isDarkMode, toggleTheme } = useThemeContext();
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [prevMode, setPrevMode] = React.useState(isDarkMode);

  const handleToggle = () => {
    if (isAnimating) return;

    setPrevMode(isDarkMode);
    setIsAnimating(true);

    setTimeout(() => {
      toggleTheme();
    }, 250);

    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  return (
    <Tooltip title={isDarkMode ? 'Ljust' : 'Mörkt'}>
      <IconButton
        onClick={handleToggle}
        disabled={isAnimating}
        size="small"
        sx={{
          color: iconColor,
          transition: 'all 0.3s ease',
          '&:hover:not(:disabled)': {
            opacity: 0.8,
          },
          position: 'relative',
          width: 40,
          height: 40,
        }}
      >
        {isAnimating ? (
          <>
            <Box sx={getAnimatedIconBoxSx(true, true)}>
              {prevMode ? (
                <WbSunny sx={{ fontSize: 24, color: iconColor }} />
              ) : (
                <NightlightRound sx={{ fontSize: 24, color: iconColor }} />
              )}
            </Box>

            <Box sx={getAnimatedIconBoxSx(true, false)}>
              {!prevMode ? (
                <WbSunny sx={{ fontSize: 24, color: iconColor }} />
              ) : (
                <NightlightRound sx={{ fontSize: 24, color: iconColor }} />
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isDarkMode ? (
              <WbSunny sx={{ fontSize: 24, color: iconColor }} />
            ) : (
              <NightlightRound sx={{ fontSize: 24, color: iconColor }} />
            )}
          </Box>
        )}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;